import { useEffect, useState } from 'react'

import { useToasts } from '@/hooks/useToasts'

import { ApolloError } from '@apollo/client'

import {
  useAiOperatorDeviceConfigurationQuery,
  useDeleteAiOperatorDeviceConfigurationMutation,
  useUpsertAiOperatorDeviceConfigurationMutation,
} from '@/graphql/generated/hooks'

export const useThresholdConfiguration = (deviceId: string) => {
  const { showSuccess, showError } = useToasts()

  const [accessGrantedThreshold, setAccessGrantedThreshold] =
    useState<number>(0)
  const [doorCloseThreshold, setDoorCloseThreshold] = useState<number>(0)
  const [initialAccessGranted, setInitialAccessGranted] = useState<number>(0)
  const [initialDoorClose, setInitialDoorClose] = useState<number>(0)

  const {
    data,
    loading: isLoading,
    refetch,
  } = useAiOperatorDeviceConfigurationQuery({
    variables: { deviceId },
    fetchPolicy: 'network-only',
  })

  const [upsertConfiguration, { loading: isUpsertLoading }] =
    useUpsertAiOperatorDeviceConfigurationMutation()

  const [deleteConfiguration, { loading: isDeleteLoading }] =
    useDeleteAiOperatorDeviceConfigurationMutation()

  const config = data?.aiOperatorDeviceConfiguration
  const isDefault = config?.isDefault ?? true

  useEffect(() => {
    if (!isLoading && config) {
      const accessValue = config.accessGrantedTimeThreshold
      const doorValue = config.doorCloseTimeThreshold
      setAccessGrantedThreshold(accessValue)
      setDoorCloseThreshold(doorValue)
      setInitialAccessGranted(accessValue)
      setInitialDoorClose(doorValue)
    }
  }, [isLoading, config])

  const isDirty =
    accessGrantedThreshold !== initialAccessGranted ||
    doorCloseThreshold !== initialDoorClose

  const onSave = async () => {
    try {
      await upsertConfiguration({
        variables: {
          input: {
            deviceId,
            accessGrantedTimeThreshold: accessGrantedThreshold,
            doorCloseTimeThreshold: doorCloseThreshold,
          },
        },
      })
      await refetch()
      setInitialAccessGranted(accessGrantedThreshold)
      setInitialDoorClose(doorCloseThreshold)
      showSuccess('Threshold configuration saved.')
    } catch (e) {
      showError(e instanceof ApolloError ? e.message : 'An error occurred')
    }
  }

  const onResetToDefaults = async () => {
    try {
      await deleteConfiguration({
        variables: {
          input: { deviceId },
        },
      })
      await refetch()
      showSuccess('Threshold configuration reset to defaults.')
    } catch (e) {
      showError(e instanceof ApolloError ? e.message : 'An error occurred')
    }
  }

  const handleAccessGrantedChange = (_: string, valueAsNumber: number) => {
    setAccessGrantedThreshold(valueAsNumber)
  }

  const handleDoorCloseChange = (_: string, valueAsNumber: number) => {
    setDoorCloseThreshold(valueAsNumber)
  }

  const isSaveDisabled = !isDirty || isUpsertLoading
  const isResetDisabled = isDefault || isDeleteLoading

  return {
    accessGrantedThreshold,
    doorCloseThreshold,
    handleAccessGrantedChange,
    handleDoorCloseChange,
    isDefault,
    isDeleteLoading,
    isLoading,
    isResetDisabled,
    isSaveDisabled,
    isUpsertLoading,
    onResetToDefaults,
    onSave,
  }
}
